import React, { useState } from 'react';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPencil,
  faShare,
  faSearch,
  faSquareArrowUpRight,
} from '@fortawesome/pro-light-svg-icons';
import CreateReportModal from './CreateReportModal';
import { type CnReport, type ReportFormData } from './types';

/**
 * Modal component for selecting, editing, and creating reports
 * Used in Charity Navigator Auth0 login flow
 */
type ReportSelectionModalProps = {
  reports: CnReport[];
  onSelectReport: (reportId: number, programId?: number) => void | Promise<void>;
  onEditReport: (report: CnReport) => void;
  onCreateReport: (data: ReportFormData) => void | Promise<void>;
  onClose?: () => void;
};

export default function ReportSelectionModal({
  reports,
  onSelectReport,
  onEditReport,
  onCreateReport,
  onClose,
}: ReportSelectionModalProps) {
  const [searchFilter, setSearchFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(reports.length === 0);
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);

  const hasExistingReport = reports.length > 0;

  // Filter reports based on search term
  const filteredReports = reports.filter(report => {
    if (!searchFilter) return true;
    const searchLower = searchFilter.toLowerCase();
    return (
      report.name?.toLowerCase().includes(searchLower) ||
      report.status?.toLowerCase().includes(searchLower) ||
      report.program_name?.toLowerCase().includes(searchLower) ||
      report.year?.toString().includes(searchLower)
    );
  });

  // Sort reports by ID descending (most recent first)
  const sortedReports = [...filteredReports].sort((a, b) => b.id - a.id);

  // Handle checkbox selection
  const handleSelectReport = (reportId: number) => {
    setSelectedReportId(selectedReportId === reportId ? null : reportId);
  };

  // Handle share report - select the report and navigate to wizard
  const handleShareReport = () => {
    if (selectedReportId != null) {
      onSelectReport(selectedReportId);
    }
  };

  // Format date for display
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // If no reports available, show create modal directly
  if (reports.length === 0 && showCreateModal) {
    return (
      <CreateReportModal
        onSave={async (data: ReportFormData) => {
          await onCreateReport(data);
        }}
        onCancel={onClose}
      />
    );
  }

  return (
    <div className="cn-modal-overlay">
      <div className="cn-modal-content">
        {/* Charity Navigator Encompass Rating Information */}
        <div className="cn-info-section">
          <p className="cn-info-text">
            It looks like you already have published reports in your True Impact account. You can choose to share one of these reports for
            your Charity Navigator Encompass Rating, or start a new impact report.
          </p>
          <p className="cn-info-text">
            At this time, we score one program per organization, though we plan to support multiple impact reports in the future. Please
            select a recent report—reports expire four years after their start date and cannot be used for the Encompass Rating.
          </p>
          <p className="cn-info-text">
            To review the content of your published reports, log in to your True Impact account. If you start a new report, we recommend
            selecting a program that best represents your organization's core work or the largest share of program-related spending.
          </p>
        </div>

        {/* Search Filter - only show if multiple reports */}
        {reports.length > 1 && (
          <div className="cn-search-container">
            <input
              type="text"
              placeholder="Search reports by name, status, program, or year..."
              value={searchFilter}
              onChange={e => setSearchFilter(e.target.value)}
              className="cn-search-input"
            />
            <button className="cn-search-icon-button" type="button">
              <FontAwesomeIcon icon={faSearch} className="cn-search-icon" />
            </button>
          </div>
        )}

        {/* Reports List */}
        <div className="cn-reports-list">
          {sortedReports.length === 0 ? (
            <p className="cn-no-results">No reports match your search.</p>
          ) : (
            sortedReports.map(report => (
              <div
                key={report.id}
                className={`cn-report-card ${
                  selectedReportId === report.id
                    ? 'cn-report-card-selected'
                    : ''
                }`}
                onClick={() => handleSelectReport(report.id)}
              >
                <div className="cn-report-card-checkbox">
                  <Checkbox
                    checked={selectedReportId === report.id}
                    onChange={() => handleSelectReport(report.id)}
                    color="primary"
                    className="cn-checkbox"
                  />
                </div>
                <div className="cn-report-card-content-new">
                  <h3 className="cn-report-card-title">{report.name}</h3>
                  <div className="cn-report-card-details-grid">
                    <div className="cn-report-card-detail-item">
                      <span className="cn-detail-label">Year:</span>
                      <span className="cn-detail-value">
                        {report.year || 'N/A'}
                      </span>
                    </div>
                    <div className="cn-report-card-detail-item">
                      <span className="cn-detail-label">Start Date:</span>
                      <span className="cn-detail-value">
                        {formatDate(report.start_date) || 'Not set'}
                      </span>
                    </div>
                    <div className="cn-report-card-detail-item">
                      <span className="cn-detail-label">End Date:</span>
                      <span className="cn-detail-value">
                        {formatDate(report.end_date) || 'Not set'}
                      </span>
                    </div>
                    <div className="cn-report-card-detail-item">
                      <span className="cn-detail-label">Program Manager:</span>
                      <span className="cn-detail-value">
                        {report.program_manager ? (
                          <>
                            {`${report.program_manager.first_name ||
                              ''} ${report.program_manager.last_name ||
                              ''}`.trim() || 'Not set'}
                            {report.program_manager.title &&
                              ` (${report.program_manager.title})`}
                          </>
                        ) : (
                          'Not set'
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Show Continue and Edit buttons only for cn_opted_in reports */}
                {report.cn_opted_in && (
                  <div className="cn-report-card-actions">
                    <IconButton
                      onClick={e => {
                        e.stopPropagation();
                        onEditReport(report);
                      }}
                      className="cn-edit-button"
                      size="small"
                    >
                      <FontAwesomeIcon icon={faPencil} />
                    </IconButton>
                    <IconButton
                      onClick={e => {
                        e.stopPropagation();
                        onSelectReport(report.id, report.program_id);
                      }}
                      className="cn-edit-button"
                      size="small"
                    >
                      <FontAwesomeIcon icon={faSquareArrowUpRight} />
                    </IconButton>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Action Buttons Row */}
        <div className="cn-action-buttons-row">
          {/* Share Report Button - Left side */}
          <Button
            onClick={handleShareReport}
            disabled={!selectedReportId}
            variant="contained"
            color="primary"
            size="medium"
            className="cn-share-button"
            startIcon={<FontAwesomeIcon icon={faShare} />}
          >
            Share Report
          </Button>

          {/* Add New Report Button - Right side. A CN account may only have
              one report, so hide this once a report already exists. */}
          {!hasExistingReport && (
            <Button
              onClick={() => setShowCreateModal(true)}
              variant="outlined"
              size="small"
              className="cn-add-new-button"
            >
              + Add New
            </Button>
          )}
        </div>
      </div>

      {/* Create Report Modal - Temporarily disabled, will be used later */}
      {showCreateModal && (
        <CreateReportModal
          onSave={async (data: ReportFormData) => {
            await onCreateReport(data);
            setShowCreateModal(false);
          }}
          // onCancel={() => setShowCreateModal(false)}
        />
      )}
    </div>
  );
}
